/**
 * Prompt Service - 관리자 프롬프트 관리 서비스
 * DynamoDB에 저장하고 localStorage는 캐시/폴백으로 사용
 */

// API URL
const API_URL = import.meta.env.VITE_API_URL || 'https://api.sedaily.ai';

// 기본 프롬프트 (초기값)
const DEFAULT_PROMPTS = {
  table: `You are an expert at converting data into tables.
Analyze the user's request and generate a clean, organized table.

Response format:
1. First, provide a brief description of the table
2. JSON format table data: {"headers": ["Column1", "Column2"], "rows": [["Value1", "Value2"], ...]}
3. The table data must be written inside a \`\`\`json code block.`,

  document: `You are a professional document writer.
Create proposals, reports, and plans according to the user's request.

Write documents with clear structure and professional tone:
- Use titles and subtitles
- Emphasize key content
- Utilize markdown formatting`,

  search: `You are an expert in information search and summarization.
Provide accurate and up-to-date information for the user's questions.

Response format:
- Summarize key information first
- Provide details in a structured format
- Include sources or references if available`,

  default: `You are an AI assistant helping journalists at Seoul Economic Daily.
You assist with various tasks: creating tables, generating documents, searching information, summarization, etc.
Always provide professional and accurate responses.`,
};

// SubType별 기본 프롬프트 (설명, 지침, 파일 구조)
// files는 배열로 저장: [{ name, content, type }]
const DEFAULT_SUBTYPE_PROMPTS = {
  // Headline Tool
  t5: {
    description: 'Classic headline generator focusing on journalism standards',
    instructions: `Generate 5 different headline styles:
1. Journalism-focused - Accurate and objective
2. SEO-optimized - Include key search terms
3. Click-driving - Create curiosity
4. Social media - Short and shareable
5. Keyword-optimized - Target specific terms`,
    files: [],
  },
  c7: {
    description: 'Click-driving headline generator for maximum engagement',
    instructions: `Generate 7 click-worthy headlines:
1. Question style - Engage reader curiosity
2. Number style - "7 reasons why..."
3. How-to style - Actionable content
4. Breaking news style - Urgency
5. Emotional hook - Connect emotionally
6. Controversial take - Spark discussion
7. Exclusive reveal - FOMO effect`,
    files: [],
  },
  // Proofread Tool
  business: {
    description: 'Business/Finance/Industry focused proofreading',
    instructions: `Focus on:
- Business terminology accuracy
- Financial data verification
- Industry jargon consistency
- Professional tone maintenance`,
    files: [],
  },
  general: {
    description: 'General news proofreading',
    instructions: `Check for:
- Grammar and spelling
- Sentence structure
- AP style guidelines
- Clarity and readability`,
    files: [],
  },
  // Press Release Tool
  gov: {
    description: 'Government press release conversion',
    instructions: `Transform government press releases:
- Simplify bureaucratic language
- Highlight citizen impact
- Add context and background`,
    files: [],
  },
  corp: {
    description: 'Corporate press release conversion',
    instructions: `Transform corporate press releases:
- Remove marketing fluff
- Focus on newsworthy elements
- Verify claims and data`,
    files: [],
  },
};

const STORAGE_KEY = 'ai_labs_admin_prompts';
const SUBTYPE_STORAGE_KEY = 'ai_labs_subtype_prompts';

class PromptService {
  constructor() {
    this.prompts = this.loadPromptsFromCache();
    this.subtypePrompts = this.loadSubtypePromptsFromCache();
    this.isInitialized = false;
    this.initPromise = null;
  }

  // ===== Initialization =====

  async initialize() {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._fetchAllPrompts();
    await this.initPromise;
    this.isInitialized = true;
  }

  async _fetchAllPrompts() {
    try {
      const response = await this._apiRequest('GET', '/prompts');
      if (response && response.success) {
        const { tools, subtypes } = response.data;

        if (tools && tools.length > 0) {
          const toolPrompts = {};
          tools.forEach((t) => {
            toolPrompts[t.toolId] = t.content;
          });
          this.prompts = { ...DEFAULT_PROMPTS, ...toolPrompts };
          this._savePromptsToCache(this.prompts);
        }

        if (subtypes && subtypes.length > 0) {
          const subtypePromptsMap = {};
          subtypes.forEach((s) => {
            subtypePromptsMap[s.subtypeId] = {
              description: s.description,
              instructions: s.instructions,
              files: s.files,
            };
          });
          this.subtypePrompts = { ...DEFAULT_SUBTYPE_PROMPTS, ...subtypePromptsMap };
          this._saveSubtypePromptsToCache(this.subtypePrompts);
        }

        console.log('Prompts loaded from backend');
      }
    } catch (error) {
      console.warn('Failed to fetch prompts from backend, using cache:', error.message);
    }
  }

  // ===== API Request Helper =====

  async _apiRequest(method, path, body = null) {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('accessToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }

    const options = {
      method,
      headers,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_URL}${path}`, options);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'API request failed');
    }

    return data;
  }

  // ===== Cache Operations (localStorage) =====

  loadPromptsFromCache() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_PROMPTS, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load prompts from cache:', error);
    }
    return { ...DEFAULT_PROMPTS };
  }

  loadSubtypePromptsFromCache() {
    try {
      const stored = localStorage.getItem(SUBTYPE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_SUBTYPE_PROMPTS, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load subtype prompts from cache:', error);
    }
    return { ...DEFAULT_SUBTYPE_PROMPTS };
  }

  _savePromptsToCache(prompts) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
    } catch (error) {
      console.error('Failed to save prompts to cache:', error);
    }
  }

  _saveSubtypePromptsToCache(prompts) {
    try {
      localStorage.setItem(SUBTYPE_STORAGE_KEY, JSON.stringify(prompts));
    } catch (error) {
      console.error('Failed to save subtype prompts to cache:', error);
    }
  }

  // ===== Tool Prompts =====

  getPrompt(tool) {
    return this.prompts[tool] || this.prompts.default;
  }

  getAllPrompts() {
    return { ...this.prompts };
  }

  async updatePrompt(tool, content) {
    try {
      await this._apiRequest('PUT', `/prompts/tools/${tool}`, { content });
      this.prompts[tool] = content;
      this._savePromptsToCache(this.prompts);
      return true;
    } catch (error) {
      console.error('Failed to update prompt:', error);
      this.prompts[tool] = content;
      this._savePromptsToCache(this.prompts);
      return false;
    }
  }

  async updateAllPrompts(prompts) {
    try {
      await this._apiRequest('POST', '/prompts/bulk', { tools: prompts });
      this.prompts = { ...this.prompts, ...prompts };
      this._savePromptsToCache(this.prompts);
      return true;
    } catch (error) {
      console.error('Failed to update all prompts:', error);
      this.prompts = { ...this.prompts, ...prompts };
      this._savePromptsToCache(this.prompts);
      return false;
    }
  }

  async resetToDefault(tool = null) {
    if (tool) {
      const defaultContent = DEFAULT_PROMPTS[tool] || DEFAULT_PROMPTS.default;
      return this.updatePrompt(tool, defaultContent);
    }
    return this.updateAllPrompts({ ...DEFAULT_PROMPTS });
  }

  getDefaultPrompt(tool) {
    return DEFAULT_PROMPTS[tool] || DEFAULT_PROMPTS.default;
  }

  getAllDefaultPrompts() {
    return { ...DEFAULT_PROMPTS };
  }

  // ===== SubType Prompts =====

  getSubtypePrompt(subtypeId) {
    const prompt = this.subtypePrompts[subtypeId] ||
      DEFAULT_SUBTYPE_PROMPTS[subtypeId] || {
        description: '',
        instructions: '',
        files: [],
      };
    if (typeof prompt.files === 'string') {
      prompt.files = prompt.files ? [{ name: 'legacy_file.txt', content: prompt.files, type: 'text' }] : [];
    }
    return prompt;
  }

  getAllSubtypePrompts() {
    return { ...this.subtypePrompts };
  }

  async updateSubtypePrompt(subtypeId, data) {
    try {
      await this._apiRequest('PUT', `/prompts/subtypes/${subtypeId}`, {
        description: data.description || '',
        instructions: data.instructions || '',
        files: data.files || '',
      });

      this.subtypePrompts[subtypeId] = {
        description: data.description || '',
        instructions: data.instructions || '',
        files: data.files || '',
      };
      this._saveSubtypePromptsToCache(this.subtypePrompts);

      return true;
    } catch (error) {
      console.error('Failed to update subtype prompt:', error);
      this.subtypePrompts[subtypeId] = {
        description: data.description || '',
        instructions: data.instructions || '',
        files: data.files || '',
      };
      this._saveSubtypePromptsToCache(this.subtypePrompts);
      return false;
    }
  }

  async resetSubtypeToDefault(subtypeId) {
    const defaultPrompt = DEFAULT_SUBTYPE_PROMPTS[subtypeId] || {
      description: '',
      instructions: '',
      files: '',
    };
    return this.updateSubtypePrompt(subtypeId, defaultPrompt);
  }

  getDefaultSubtypePrompt(subtypeId) {
    return (
      DEFAULT_SUBTYPE_PROMPTS[subtypeId] || {
        description: '',
        instructions: '',
        files: [],
      }
    );
  }

  // SubType 프롬프트를 시스템 프롬프트로 조합
  buildSystemPrompt(subtypeId, basePrompt = '') {
    const subtype = this.getSubtypePrompt(subtypeId);
    let systemPrompt = basePrompt;

    if (subtype.description) {
      systemPrompt += `\n\n## Description\n${subtype.description}`;
    }
    if (subtype.instructions) {
      systemPrompt += `\n\n## Instructions\n${subtype.instructions}`;
    }
    if (subtype.files && Array.isArray(subtype.files) && subtype.files.length > 0) {
      systemPrompt += `\n\n## Reference Files`;
      subtype.files.forEach((file) => {
        systemPrompt += `\n\n### ${file.name} (${file.type})\n\`\`\`\n${file.content}\n\`\`\``;
      });
    } else if (typeof subtype.files === 'string' && subtype.files) {
      systemPrompt += `\n\n## Reference Files\n${subtype.files}`;
    }

    return systemPrompt.trim();
  }
}

// 싱글톤 인스턴스
const promptService = new PromptService();

export default promptService;
export { DEFAULT_PROMPTS, DEFAULT_SUBTYPE_PROMPTS };